import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZOOM_PRESETS, applyZoom, zoomFromExtremes } from './useChartZoom';

export const SYNC_TRIGGER = 'sync';
// User gestures worth mirroring onto the sibling charts. Highcharts tags drag-zoom 'zoom', mouse pan 'pan',
// wheel 'mousewheel' — and a PINCH with the raw touch event type ('touchmove'/'touchend', Pointer.pinch).
export const GESTURE_TRIGGERS = new Set(['zoom', 'pan', 'mousewheel', 'touchmove', 'touchend']);

/** Apply a preset to every mounted chart (they share the same data, so the windows line up). */
export function applySharedZoom(chartRefs, hours) {
    chartRefs.forEach((r) => { const chart = r.current && r.current.chart; if (chart) applyZoom(chart, hours); });
}

/** Mirror one chart's extremes onto the others (null = reset). Tagged so the receivers don't echo it back. */
export function syncExtremes(chartRefs, source, min, max) {
    chartRefs.forEach((r) => {
        const chart = r.current && r.current.chart;
        if (!chart || chart === source) return;
        const axis = chart.xAxis && chart.xAxis[0];
        if (!axis) return;
        if (axis.min === min && axis.max === max) return;
        axis.setExtremes(min, max, true, false, { trigger: SYNC_TRIGGER });
    });
}

/**
 * One zoom state for several stacked charts WITHOUT a navigator (mobile): the
 * All/1h/3h presets are applied to every chart, and a pinch/pan on any one of
 * them is mirrored onto the others via afterSetExtremes. The active preset is
 * derived from the extremes so gestures update the buttons too.
 *
 * @param resetKey     changes → back to `initialZoom` (new range/station)
 * @param dataKey      changes → keep the preset, slide it to the newest data (background refresh)
 * @param initialZoom  preset applied on mount and on reset ('all' | '1h' | '3h'); phones default to 3h
 */
export default function useSharedZoom(count, resetKey, dataKey, initialZoom = 'all') {
    const refsRef = useRef(null);
    if (!refsRef.current) refsRef.current = Array.from({ length: count }, () => createRef());
    const [zoom, setZoomState] = useState(initialZoom);
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;

    const setZoom = useCallback((key) => {
        const preset = ZOOM_PRESETS.find((z) => z.key === key);
        if (!preset) return;
        applySharedZoom(refsRef.current, preset.hours);
        setZoomState(key);
    }, []);

    // On mount the charts have no data yet, so this only sets the state; the dataKey effect below applies it once points arrive.
    useEffect(() => { setZoom(initialZoom); }, [resetKey, setZoom, initialZoom]);
    const lastReset = useRef(resetKey);
    useEffect(() => {
        if (lastReset.current !== resetKey) { lastReset.current = resetKey; return; }
        if (dataKey !== undefined && zoomRef.current !== 'all') setZoom(zoomRef.current);
    }, [dataKey, resetKey, setZoom]);

    // A pinch fires many afterSetExtremes per second; mirror at most once per frame.
    const pending = useRef(null);
    const xAxisEvents = useMemo(() => ({
        afterSetExtremes(e) {
            // An empty chart (mounted before its data) reports no data extremes; deriving 'all' from that would
            // overwrite the initial preset before it can be applied. Ignore until there is data.
            if (!e || !Number.isFinite(e.dataMin) || !Number.isFinite(e.dataMax)) return;
            const min = e.userMin ?? e.min; const max = e.userMax ?? e.max;
            setZoomState(zoomFromExtremes({ min, max, dataMin: e.dataMin, dataMax: e.dataMax }));
            if (!GESTURE_TRIGGERS.has(e.trigger)) return; // presets already hit every chart; sync echoes are ignored
            const source = this && this.chart; // `this` is the axis that fired
            const first = !pending.current;
            pending.current = { source, min, max };
            if (first) {
                requestAnimationFrame(() => {
                    const p = pending.current; pending.current = null;
                    if (p) syncExtremes(refsRef.current, p.source, p.min, p.max);
                });
            }
        },
    }), []);

    return { chartRefs: refsRef.current, zoom, setZoom, xAxisEvents };
}
